const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

export function isYoutubeVideoId(value: string): boolean {
  return VIDEO_ID_RE.test(value);
}

/** Medium-quality poster used on the landing recent list (no API key). */
export function youtubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/mqdefault.jpg`;
}

/**
 * Parse a YouTube watch / share / embed / shorts / live URL (or bare id) to an 11-char video id.
 * Returns null when the input is not a recognizable YouTube video reference.
 */
export function parseYoutubeVideoId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (isYoutubeVideoId(trimmed)) return trimmed;

  let url: URL;
  try {
    url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./, '');

  if (host === 'youtu.be') {
    const id = url.pathname.split('/').filter(Boolean)[0] ?? '';
    // strip accidental query-like suffix in path
    const clean = id.split('?')[0] ?? '';
    return isYoutubeVideoId(clean) ? clean : null;
  }

  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com' || host === 'youtube-nocookie.com') {
    const fromQuery = url.searchParams.get('v');
    if (fromQuery && isYoutubeVideoId(fromQuery)) return fromQuery;

    const parts = url.pathname.split('/').filter(Boolean);
    const kind = parts[0];
    const id = parts[1];
    if (kind && id && ['embed', 'shorts', 'live', 'v', 'e'].includes(kind) && isYoutubeVideoId(id)) {
      return id;
    }
  }

  return null;
}

/**
 * Read a video id from a location search string.
 *
 * Supports:
 * - `?v=<11-char id>` (mobile share / History API convention)
 * - `?vid=<id|youtube-url>` (ref/unisubs-mobile deep links; value may be
 *   percent-encoded or a raw watch URL containing nested `?` / `&`)
 */
export function readVideoIdFromSearch(search: string): string | null {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);

  const fromV = params.get('v');
  if (fromV && isYoutubeVideoId(fromV)) return fromV;

  const fromVid = params.get('vid');
  if (fromVid) {
    const parsed = parseYoutubeVideoId(fromVid.trim());
    if (parsed) return parsed;
  }

  // Unencoded `?vid=https://…/watch?v=ID` breaks URLSearchParams (nested `?`).
  // Match the raw segment like ref starter.js `normalizeStartupVidQueryParam`.
  const match = `?${raw}`.match(/[?&]vid=([^&#]*)/);
  if (match?.[1]) {
    let decoded = match[1];
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      // keep raw
    }
    const parsed = parseYoutubeVideoId(decoded.trim());
    if (parsed) return parsed;
  }

  return null;
}

/** Build a shareable `?v=<id>` search string. */
export function buildVideoSearch(videoId: string): string {
  return `?v=${encodeURIComponent(videoId)}`;
}
